import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AppLoadingService {
  // Signal-based loading state (modern approach)
  private readonly _isLoading = signal(true);
  private readonly _loadingMessage = signal('Loading application...');
  private readonly _loadingProgress = signal(0);

  // Observable-based loading state (for compatibility)
  private readonly _isLoadingSubject = new BehaviorSubject<boolean>(true);
  private readonly _loadingMessageSubject = new BehaviorSubject<string>('Loading application...');
  private readonly _loadingProgressSubject = new BehaviorSubject<number>(0);

  // Public readonly signals
  public readonly isLoading = this._isLoading.asReadonly();
  public readonly loadingMessage = this._loadingMessage.asReadonly();
  public readonly loadingProgress = this._loadingProgress.asReadonly();

  // Public observables
  public readonly isLoading$ = this._isLoadingSubject.asObservable();
  public readonly loadingMessage$ = this._loadingMessageSubject.asObservable();
  public readonly loadingProgress$ = this._loadingProgressSubject.asObservable();

  // Combined loading state
  public readonly loadingState$ = combineLatest([
    this.isLoading$,
    this.loadingMessage$,
    this.loadingProgress$
  ]).pipe(
    map(([isLoading, message, progress]) => ({
      isLoading,
      message,
      progress
    }))
  );

  // Track individual loading operations
  private loadingOperations = new Map<string, boolean>();

  /**
   * Set the overall loading state
   */
  setLoading(loading: boolean, message?: string): void {
    this._isLoading.set(loading);
    this._isLoadingSubject.next(loading);
    
    if (message) {
      this.setLoadingMessage(message);
    }
  }

  /**
   * Set the loading message
   */
  setLoadingMessage(message: string): void {
    this._loadingMessage.set(message);
    this._loadingMessageSubject.next(message);
  }

  /**
   * Set the loading progress (0-100)
   */
  setLoadingProgress(progress: number): void {
    const clampedProgress = Math.max(0, Math.min(100, progress));
    this._loadingProgress.set(clampedProgress);
    this._loadingProgressSubject.next(clampedProgress);
  }

  /**
   * Start a named loading operation
   */
  startOperation(operationName: string, message?: string): void {
    this.loadingOperations.set(operationName, true);
    this.updateLoadingState();
    
    if (message) {
      this.setLoadingMessage(message);
    }
  }

  /**
   * Complete a named loading operation
   */
  completeOperation(operationName: string): void {
    this.loadingOperations.delete(operationName);
    this.updateLoadingState();
  }

  /**
   * Complete multiple operations at once
   */
  completeOperations(operationNames: string[]): void {
    operationNames.forEach(name => {
      this.loadingOperations.delete(name);
    });
    this.updateLoadingState();
  }

  /**
   * Check if a specific operation is loading
   */
  isOperationLoading(operationName: string): boolean {
    return this.loadingOperations.has(operationName);
  }

  /**
   * Get all active loading operations
   */
  getActiveOperations(): string[] {
    return Array.from(this.loadingOperations.keys());
  }

  /**
   * Simulate app initialization with progress updates
   */
  async initializeApp(): Promise<void> {
    this.setLoading(true, 'Initializing application...');
    this.setLoadingProgress(0);

    // Simulate different initialization steps
    const steps = [
      { name: 'Loading configuration...', duration: 300, progress: 20 },
      { name: 'Authenticating user...', duration: 500, progress: 40 },
      { name: 'Loading user preferences...', duration: 200, progress: 60 },
      { name: 'Initializing services...', duration: 400, progress: 80 },
      { name: 'Finalizing setup...', duration: 300, progress: 100 }
    ];

    for (const step of steps) {
      this.setLoadingMessage(step.name);
      await this.delay(step.duration);
      this.setLoadingProgress(step.progress);
    }

    // Small delay to show completion
    await this.delay(200);
    
    this.setLoading(false, 'Application ready');
  }

  /**
   * Force complete all loading operations
   */
  forceComplete(): void {
    this.loadingOperations.clear();
    this.setLoading(false, 'Application ready');
    this.setLoadingProgress(100);
  }

  /**
   * Reset loading state
   */
  reset(): void {
    this.loadingOperations.clear();
    this.setLoading(true, 'Loading application...');
    this.setLoadingProgress(0);
  }

  /**
   * Update loading state based on active operations
   */
  private updateLoadingState(): void {
    const hasActiveOperations = this.loadingOperations.size > 0;
    
    if (!hasActiveOperations && this._isLoading()) {
      this.setLoading(false, 'Application ready');
      this.setLoadingProgress(100);
    } else if (hasActiveOperations && !this._isLoading()) {
      this.setLoading(true);
    }
  }

  /**
   * Utility method for delays
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Track a promise and show loading state
   */
  trackPromise<T>(
    promise: Promise<T>, 
    operationName: string, 
    message?: string
  ): Promise<T> {
    this.startOperation(operationName, message);
    
    return promise.finally(() => {
      this.completeOperation(operationName);
    });
  }

  /**
   * Track an observable and show loading state
   */
  trackObservable<T>(
    observable: Observable<T>,
    operationName: string,
    message?: string
  ): Observable<T> {
    this.startOperation(operationName, message);
    
    return new Observable<T>(subscriber => {
      const subscription = observable.subscribe({
        next: value => subscriber.next(value),
        error: error => {
          this.completeOperation(operationName);
          subscriber.error(error);
        },
        complete: () => {
          this.completeOperation(operationName);
          subscriber.complete();
        }
      });

      return () => subscription.unsubscribe();
    });
  }
} 